const STORAGE_KEY = "nativePdfMode";

/**
 * True when the page was opened with `?isMobile` — the host app wants to
 * present PDFs itself rather than have the exam render them.
 *
 * The flag is latched into sessionStorage on first sight: it arrives on the
 * deep link (e.g. /cbq-exam/start/:examId?isMobile=1) but in-app navigation to
 * /cbq-exam/:sessionId drops the query string, and the PDF buttons live on the
 * pages *after* that navigation.
 */
export const isNativeMode = (): boolean => {
  try {
    if (new URLSearchParams(window.location.search).has("isMobile")) {
      sessionStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // Private-mode webviews can throw on storage access.
    return new URLSearchParams(window.location.search).has("isMobile");
  }
};

/**
 * Append `isMobile` to an in-app path when the session is running under the
 * host app, so the flag survives a reload or a fresh webview context — the
 * sessionStorage latch only covers navigation within one page load.
 */
export const withNativeParam = (path: string): string => {
  if (!isNativeMode()) return path;
  const [base, hash] = path.split("#");
  const joined = `${base}${base.includes("?") ? "&" : "?"}isMobile=1`;
  return hash ? `${joined}#${hash}` : joined;
};

export type NativePdfMessage = {
  type: "open-pdf";
  url: string;
  title: string;
};

/**
 * Hand a PDF URL to the native host. Tries the React Native webview bridge
 * first, then falls back to postMessage on the parent frame.
 */
export const sendPdfToHost = (url: string, title: string) => {
  const message: NativePdfMessage = { type: "open-pdf", url, title };
  const payload = JSON.stringify(message);

  const bridge = (
    window as unknown as {
      ReactNativeWebView?: { postMessage: (data: string) => void };
    }
  ).ReactNativeWebView;

  if (bridge?.postMessage) {
    bridge.postMessage(payload);
    return;
  }

  try {
    window.parent?.postMessage(payload, "*");
  } catch (error) {
    console.error("Could not hand PDF to host:", error);
  }
};
